import type { UseQueryResult } from '@tanstack/react-query'
import { useQuery } from '@tanstack/react-query'

import { api } from '@/lib/utils/api.utils'
import { getHyperBackendUrl } from '../utils/client.utils'

enum ERROR_CODE {
  NETWORK_ERROR = '50001',
  EMPTY_DATA = '50002',
}

export interface MarketTickerData {
  symbol: string
  name: string
  icon: string
  last_price: number
  index_price: number
  mark_price: number
  change_24h_amount: number
  change_24h_percent: number
  volume_24h: number
  funding_rate: number
  funding_8h: number
  next_funding_time: string
  open_interest: number
  price_precision: number
  amount_precision: number
  tags: string[]
  source: string
  created_at: string
  updated_at: string
  is_favorite: boolean
}

/**
 * 获取市场行情数据
 * @param symbol 交易对符号，例如 "BTC"
 * @param userId 用户ID（可选）
 * @returns 市场行情数据
 */
export async function requestMarketTicker(symbol: string, userId?: string): Promise<MarketTickerData | undefined> {
  try {
    const searchParams: Record<string, string> = {}
    if (userId) {
      searchParams.user_id = userId
    }

    const response = await api.get(`${getHyperBackendUrl()}/api/market/market/tickers/${symbol}`, {
      searchParams,
    })

    if (!response.ok) {
      throw new Error(ERROR_CODE.NETWORK_ERROR)
    }

    const data = await response.json<MarketTickerData>()
    return data
  }
  catch (error: unknown) {
    console.error('Failed to fetch market ticker:', error)
    throw error
  }
}

/**
 * 使用 React Query 获取市场行情数据的 Hook
 * @param symbol 交易对符号
 * @param userId 用户ID（可选）
 * @returns 查询结果
 */
export function useMarketTicker(symbol: string, userId?: string): UseQueryResult<MarketTickerData | undefined, Error> {
  return useQuery({
    queryKey: ['market_ticker', symbol, userId],
    queryFn: () => requestMarketTicker(symbol, userId),
    refetchInterval: 5000, // 每5秒刷新一次数据
    staleTime: 3000, // 3秒内认为数据是新鲜的
    enabled: !!symbol, // 只有当symbol存在时才启用查询
  })
}

/**
 * 获取所有市场行情数据列表
 * @param userId 用户ID（可选）
 * @param prefix 搜索前缀（可选）
 * @returns 市场行情数据列表
 */
export async function requestMarketTickers(userId?: string, prefix?: string): Promise<MarketTickerData[]> {
  try {
    const searchParams: Record<string, string> = {}
    if (userId) {
      searchParams.user_id = userId
    }
    if (prefix) {
      searchParams.prefix = prefix
    }

    const response = await api.get(`${getHyperBackendUrl()}/api/market/market/tickers`, {
      searchParams,
    })

    if (!response.ok) {
      throw new Error(ERROR_CODE.NETWORK_ERROR)
    }

    const data = await response.json<MarketTickerData[]>()
    return data || []
  }
  catch (error: unknown) {
    console.error('Failed to fetch market tickers:', error)
    throw error
  }
}

/**
 * 使用 React Query 获取市场行情数据列表的 Hook
 * @param userId 用户ID（可选）
 * @param prefix 搜索前缀（可选）
 * @param enabled 是否启用查询
 * @returns 查询结果
 */
export function useMarketTickers(
  userId?: string,
  prefix?: string,
  enabled = true,
): UseQueryResult<MarketTickerData[], Error> {
  return useQuery({
    queryKey: ['market_tickers', userId, prefix],
    queryFn: () => requestMarketTickers(userId, prefix),
    refetchInterval: 10000, // 每10秒自动刷新
    staleTime: 8000, // 8秒内认为数据是新鲜的
    gcTime: 5 * 60 * 1000, // 5分钟缓存时间
    refetchOnWindowFocus: true, // 窗口聚焦时重新获取
    refetchOnMount: true, // 组件挂载时重新获取
    enabled,
  })
}

/**
 * 添加到收藏
 * @param symbol 交易对符号
 * @param userId 用户ID（可选）
 * @returns Promise
 */
export async function addToFavorites(symbol: string, userId?: string): Promise<void> {
  try {
    const searchParams: Record<string, string> = {}
    if (userId) {
      searchParams.user_id = userId
    }

    const response = await api.post(`${getHyperBackendUrl()}/api/market/market/favorites`, {
      json: { symbol },
      searchParams,
    })

    if (!response.ok) {
      throw new Error(ERROR_CODE.NETWORK_ERROR)
    }
  }
  catch (error: unknown) {
    console.error('Failed to add to favorites:', error)
    throw error
  }
}

/**
 * 从收藏中移除
 * @param symbol 交易对符号
 * @param userId 用户ID（可选）
 * @returns Promise
 */
export async function removeFromFavorites(symbol: string, userId?: string): Promise<void> {
  try {
    const searchParams: Record<string, string> = {}
    if (userId) {
      searchParams.user_id = userId
    }

    const response = await api.post(`${getHyperBackendUrl()}/api/market/market/favorites/remove`, {
      json: { symbol },
      searchParams,
    })

    if (!response.ok) {
      throw new Error(ERROR_CODE.NETWORK_ERROR)
    }
  }
  catch (error: unknown) {
    console.error('Failed to remove from favorites:', error)
    throw error
  }
}
